import { useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { upload } from "../api/client";
import type { Project } from "../api/types";
import { Icon } from "./icons";
import { ScriptMarksSummary } from "./ScriptMarksSummary";
import { toast } from "./toast";

/**
 * The manuscript as an *asset* you can drop in (or replace) at any time —
 * independent of audio and of any service. With no manuscript a project can
 * still run a spec check; add one here when you're ready to proof against it.
 */
export function ManuscriptCard({
  projectId,
  filename,
  onChanged,
}: {
  projectId: string;
  filename: string | null;
  onChanged: (project: Project) => void;
}) {
  const qc = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  const [pct, setPct] = useState(0);

  async function send(file: File) {
    if (!/\.(pdf|docx|doc)$/i.test(file.name)) {
      toast("That isn't a PDF or Word file", "error");
      return;
    }
    setBusy(true);
    setPct(0);
    try {
      const form = new FormData();
      form.append("file", file);
      const project = await upload<Project>(
        `/api/projects/${projectId}/manuscript`,
        form,
        (p) => setPct(p)
      );
      toast(filename ? "Manuscript replaced." : "Manuscript added.", "success");
      onChanged(project);
      qc.invalidateQueries({ queryKey: ["script-marks", projectId] });   // a new script: read it afresh
    } catch (e: any) {
      toast(e.message || "Couldn't upload the manuscript.", "error");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card asset-card">
      <div className="asset-head">
        <span className="asset-ic"><Icon name="document" size={18} /></span>
        <div className="asset-title">
          <strong>Manuscript</strong>
          <span className="muted asset-sub">
            {filename ? "The PDF proofing compares the narration against." : "Optional — only needed to proof against."}
          </span>
        </div>
        {filename && (
          <Link to={`/projects/${projectId}/manuscript`} className="btn ghost sm">
            <Icon name="file-text" size={15} /> Review
          </Link>
        )}
      </div>

      <input
        ref={input}
        type="file"
        accept="application/pdf,.pdf,.docx,.doc"
        hidden
        onChange={(e) => { if (e.target.files?.[0]) send(e.target.files[0]); e.currentTarget.value = ""; }}
      />

      {filename ? (
        <div className="asset-have">
          <span className="row" style={{ gap: 8, minWidth: 0 }}>
            <Icon name="document" size={16} />
            <span className="mono ellipsis">{filename}</span>
          </span>
          <button className="btn ghost sm" disabled={busy} onClick={() => input.current?.click()}>
            {busy ? `Replacing… ${pct}%` : "Replace"}
          </button>
        </div>
      ) : null}
      {filename && <ScriptMarksSummary projectId={projectId} compact />}
      {filename ? null : (
        <div
          className={`dropzone slim ${drag ? "drag" : ""}`}
          onClick={() => !busy && input.current?.click()}
          onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
          onDragLeave={() => setDrag(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDrag(false);
            const f = Array.from(e.dataTransfer.files).find((x) => /\.(pdf|docx|doc)$/i.test(x.name));
            if (f) send(f);
            else toast("That isn't a PDF or Word file", "error");
          }}
        >
          <div className="hint">
            {busy ? `Uploading… ${pct}%` : "Drop the manuscript (PDF or Word) here, or click to choose"}
          </div>
        </div>
      )}
    </div>
  );
}
